/* ===============================================
  #Collection browse state

  The collection grid pages in with AJAX (load more / infinite scroll) without
  touching the address bar, so a normal back navigation from a product page
  re-renders page 1 and drops the scroll position.

  This records how far the shopper had browsed against the collection's own
  history entry, and replays it when that entry is returned to.
================================================== */

(function() {
  'use strict';

  var STORAGE_PREFIX = 'titleys:collection-state:';
  var HISTORY_KEY = 'titleysCollectionState';
  var MAX_PAGES = 10;          // cap the replay so a deep browse can't stampede the server
  var TTL = 30 * 60 * 1000;    // ignore anything older than 30 minutes

  var GRID = '.product-list--collection';
  var ITEM = '[data-load-more--grid-item]';
  var MORE_LINK = '[data-load-more]';
  var MORE_WRAP = '.js-load-more';

  if (!window.history || !window.history.replaceState || !window.fetch) return;

  // Product pages live under /collections/<handle>/products/<handle> too.
  var path = window.location.pathname;
  if (path.indexOf('/collections/') === -1 || path.indexOf('/products/') !== -1) return;

  function paginationType() {
    return (window.PXUTheme
      && window.PXUTheme.theme_settings
      && window.PXUTheme.theme_settings.pagination_type) || '';
  }

  // Only the AJAX pagination modes lose state; basic pagination keeps it in the URL.
  var pagination = paginationType();
  if (pagination !== 'load_more' && pagination !== 'load_more_button' && pagination !== 'infinite_scroll') return;

  /* -------------------------------------------------- storage helpers */

  function readState(token) {
    try {
      var raw = window.sessionStorage.getItem(STORAGE_PREFIX + token);
      return raw ? JSON.parse(raw) : null;
    } catch (e) {
      return null;
    }
  }

  function writeState(token, state) {
    try {
      window.sessionStorage.setItem(STORAGE_PREFIX + token, JSON.stringify(state));
    } catch (e) {
      /* quota or private mode - browsing simply won't be restored */
    }
  }

  function pruneState() {
    try {
      var now = Date.now();
      var stale = [];
      for (var i = 0; i < window.sessionStorage.length; i++) {
        var key = window.sessionStorage.key(i);
        if (!key || key.indexOf(STORAGE_PREFIX) !== 0) continue;
        var entry = null;
        try {
          entry = JSON.parse(window.sessionStorage.getItem(key));
        } catch (e) { /* unparseable - drop it */ }
        if (!entry || !entry.ts || now - entry.ts > TTL) stale.push(key);
      }
      for (var j = 0; j < stale.length; j++) window.sessionStorage.removeItem(stale[j]);
    } catch (e) {
      /* ignore */
    }
  }

  /* -------------------------------------------------- identity */

  // The collection as the shopper sees it: path plus filters and sort, minus the page.
  function collectionKey() {
    var params = new URLSearchParams(window.location.search);
    params.delete('page');
    var query = params.toString();
    return window.location.pathname + (query ? '?' + query : '');
  }

  function historyToken() {
    return (window.history.state && window.history.state[HISTORY_KEY]) || null;
  }

  function ensureHistoryToken() {
    var token = historyToken();
    if (token) return token;
    token = 'c' + Date.now().toString(36) + Math.random().toString(36).slice(2, 8);
    var state = {};
    if (window.history.state && typeof window.history.state === 'object') {
      for (var key in window.history.state) {
        if (Object.prototype.hasOwnProperty.call(window.history.state, key)) {
          state[key] = window.history.state[key];
        }
      }
    }
    state[HISTORY_KEY] = token;
    try {
      window.history.replaceState(state, '', window.location.href);
    } catch (e) {
      return null;
    }
    return token;
  }

  /* -------------------------------------------------- DOM helpers */

  function grid() { return document.querySelector(GRID); }

  function itemCount() {
    var node = grid();
    return node ? node.querySelectorAll(ITEM).length : 0;
  }

  function moreLink() { return document.querySelector(MORE_LINK); }

  // The theme keeps the shopper's progress in the Load more link: its href is
  // always the page that has not been fetched yet. Item counts can't be trusted
  // - the theme's infinite scroll appends a whole grid at a time and can double
  // up - but this number is the theme's own bookmark.
  function nextPageFromDom() {
    var link = moreLink();
    // Infinite scroll deletes the link at the end of the collection; the button
    // mode only hides it, leaving a stale href behind.
    if (!link || link.classList.contains('is-hidden')) return null;
    var href = link.getAttribute('href');
    if (!href) return null;
    var page = parseInt(new URL(href, window.location.origin).searchParams.get('page'), 10);
    return isNaN(page) ? null : page;
  }

  // Once the link is gone there is nothing left to read the page count from, so
  // keep the last number it advertised.
  var furthestPage = 1;
  function notePageProgress() {
    var next = nextPageFromDom();
    if (next && next > furthestPage) furthestPage = next;
  }
  notePageProgress();
  var progressTimer = null;
  window.addEventListener('scroll', function() {
    if (progressTimer) return;
    progressTimer = window.setTimeout(function() {
      progressTimer = null;
      notePageProgress();
    }, 300);
  }, { passive: true });

  // Grid links can be relative, absolute, or carry a ?variant= suffix. The path
  // is the only part that identifies the product across page loads.
  function productPath(href) {
    if (!href) return null;
    try {
      return new URL(href, window.location.origin).pathname;
    } catch (e) {
      return href;
    }
  }

  function pageUrl(page) {
    var url = new URL(window.location.href);
    url.searchParams.set('page', String(page));
    return url.pathname + url.search;
  }

  function showLoading(on) {
    var icon = document.querySelector('.load-more__icon');
    if (!icon) return;
    if (on) {
      icon.classList.add('loading-in-progress');
      icon.style.height = '40px';
      icon.style.width = '40px';
      icon.style.opacity = '1';
    } else {
      icon.classList.remove('loading-in-progress');
      icon.style.height = '';
      icon.style.width = '';
      icon.style.opacity = '';
    }
  }

  /* -------------------------------------------------- saving */

  var pendingSave = null;

  function snapshot(productUrl, viewportTop) {
    if (!itemCount()) return null;
    notePageProgress();
    var next = nextPageFromDom();
    // next === null means the shopper reached the end of the collection, so the
    // last page the link pointed at is the last page that got loaded.
    var pagesLoaded = next === null ? furthestPage : next - 1;
    // Nothing worth replaying: still on page one and no product was opened.
    if (pagesLoaded < 2 && !productUrl) return null;
    return {
      key: collectionKey(),
      pages: Math.max(1, Math.min(pagesLoaded, MAX_PAGES)),
      exhausted: next === null,
      scrollY: window.pageYOffset,
      product: productUrl || null,
      // Where the product sat on screen when they clicked it. Putting it back in
      // the same place means the page looks unchanged from the shopper's side.
      viewportTop: typeof viewportTop === 'number' ? Math.round(viewportTop) : null,
      ts: Date.now()
    };
  }

  function save(productUrl, viewportTop) {
    var pending = pendingSave || {};
    var state = snapshot(
      productUrl || pending.product,
      typeof viewportTop === 'number' ? viewportTop : pending.viewportTop
    );
    if (!state) return;
    var token = ensureHistoryToken();
    if (!token) return;
    pruneState();
    writeState(token, state);
  }

  // Capture phase so this wins even if something downstream swallows the click.
  document.addEventListener('click', function(event) {
    var link = event.target && event.target.closest ? event.target.closest('a[href]') : null;
    if (!link) return;
    var node = grid();
    if (!node || !node.contains(link)) return;
    if (link.getAttribute('href').indexOf('/products/') === -1) return;
    var item = link.closest(ITEM) || link;
    pendingSave = {
      product: productPath(link.getAttribute('href')),
      viewportTop: item.getBoundingClientRect().top
    };
    save(pendingSave.product, pendingSave.viewportTop);
  }, true);

  window.addEventListener('pagehide', function() {
    save();
  });

  /* -------------------------------------------------- restoring */

  // Back or forward only. Following a link to the same collection is a brand new
  // history entry and has to behave like any other first visit. theme.liquid
  // makes the same check inline, to decide whether to paint the cover.
  function cameFromHistory() {
    try {
      var entries = window.performance
        && window.performance.getEntriesByType
        && window.performance.getEntriesByType('navigation');
      if (entries && entries.length) return entries[0].type === 'back_forward';
      return !!(window.performance && window.performance.navigation
        && window.performance.navigation.type === 2);
    } catch (e) {
      return false;
    }
  }

  var restoreToken = cameFromHistory() ? historyToken() : null;
  var restoreState = restoreToken ? readState(restoreToken) : null;

  if (restoreState && (restoreState.key !== collectionKey() || Date.now() - restoreState.ts > TTL)) {
    restoreState = null;
  }

  // The browser's remembered offset belongs to the un-restored page one, so take
  // scroll restoration away from it. theme.liquid does this inline in the head as
  // well, because Chrome can restore before a deferred script has even run.
  function manualScrollRestoration() {
    try { window.history.scrollRestoration = 'manual'; } catch (e) { /* unsupported */ }
  }
  if (restoreState) manualScrollRestoration();

  // Once the shopper touches the page it is theirs: stop repositioning and get
  // the curtain out of their way.
  var userTookOver = false;
  function noteUserInput() {
    if (userTookOver) return;
    userTookOver = true;
    hideCurtain();
  }
  ['wheel', 'touchstart', 'keydown', 'mousedown'].forEach(function(type) {
    window.addEventListener(type, noteUserInput, { passive: true });
  });

  /* -------------------------------------------------- the cover */

  // The cover itself is CSS in theme.liquid, switched on by a class on <html>
  // before the first paint. All this file does is take it back down - by the
  // time a deferred script runs, painting it here would already be too late.
  var COVER = 'titleys-resuming';
  var COVER_OUT = 'titleys-resuming--out';
  var coverShownAt = 0;
  var MIN_COVER_MS = 300;   // long enough that a quick restore reads as deliberate

  function coverIsUp() {
    return document.documentElement.className.indexOf(COVER) !== -1;
  }

  function dropClass(name) {
    var html = document.documentElement;
    html.className = html.className.split(/\s+/).filter(function(c) {
      return c && c !== name;
    }).join(' ');
  }

  function hideCurtain() {
    if (!coverIsUp()) return;
    var wait = Math.max(0, MIN_COVER_MS - (Date.now() - coverShownAt));
    window.setTimeout(function() {
      document.documentElement.className += ' ' + COVER_OUT;
      window.setTimeout(function() {
        dropClass(COVER);
        dropClass(COVER_OUT);
      }, 240);
    }, wait);
  }

  /* -------------------------------------------------- putting them back */

  function targetElement(state) {
    if (!state.product) return null;
    var node = grid();
    if (!node) return null;
    var links = node.querySelectorAll('a[href*="/products/"]');
    for (var i = 0; i < links.length; i++) {
      if (productPath(links[i].getAttribute('href')) === state.product) {
        return links[i].closest(ITEM) || links[i];
      }
    }
    return null;
  }

  function documentTop(el) {
    return el.getBoundingClientRect().top + window.pageYOffset;
  }

  // One move, to the position the shopper actually left the product in. Falling
  // back to the middle of the screen if that offset is unusable - a rotated
  // phone, say, where the old offset would put the product off screen.
  function wantedScrollY(state, target) {
    var height = target.getBoundingClientRect().height;
    var offset = state.viewportTop;
    // An unusable saved offset - a rotated phone, a resized window - would put the
    // product off screen, so fall back to the middle.
    var usable = typeof offset === 'number' && offset > -height && offset < window.innerHeight;
    if (!usable) offset = (window.innerHeight - height) / 2;
    return Math.max(0, Math.round(documentTop(target) - offset));
  }

  function placeOnce(state, target) {
    window.scrollTo(0, wantedScrollY(state, target));
  }

  // Apps and widgets keep injecting things above the grid for a few seconds after
  // load, which shifts the whole page under the shopper.
  //
  // This re-asserts the one thing that should stay true - the product sits where
  // they left it - rather than accumulating a running delta. A delta chases every
  // transient shift and keeps the error when the content springs back; measuring
  // against a fixed reference cannot drift, and is a no-op whenever nothing has
  // moved, so it never causes a hop of its own.
  //
  // A timer rather than requestAnimationFrame, which a background tab throttles
  // to a standstill.
  function anchorTo(state, target) {
    var deadline = Date.now() + 4000;
    var timer = window.setInterval(function() {
      if (userTookOver || Date.now() > deadline) {
        window.clearInterval(timer);
        return;
      }
      var wanted = wantedScrollY(state, target);
      if (Math.abs(wanted - window.pageYOffset) >= 0.5) window.scrollTo(0, wanted);
    }, 50);
  }

  function settle(state) {
    var target = targetElement(state);

    if (!target) {
      window.scrollTo(0, state.scrollY || 0);
      hideCurtain();
      return;
    }

    // getBoundingClientRect forces the layout the appended rows need, so the first
    // placement is already accurate.
    placeOnce(state, target);
    anchorTo(state, target);

    // Apps inject above the grid right about now, and the correction for that -
    // however quick - is a twitch if the shopper can see it. Wait until the
    // position has held still for a few ticks, so the settling happens behind the
    // cover. Capped, so a page that never stops moving still gets revealed.
    var stable = 0;
    var waited = 0;
    var watch = window.setInterval(function() {
      waited += 50;
      var onTarget = Math.abs(wantedScrollY(state, target) - window.pageYOffset) < 0.5;
      stable = onTarget ? stable + 1 : 0;
      if (stable >= 4 || waited >= 900 || userTookOver) {
        window.clearInterval(watch);
        hideCurtain();
      }
    }, 50);
  }

  function reinitialiseProducts($items) {
    var settings = (window.PXUTheme && window.PXUTheme.theme_settings) || {};
    var steps = [
      function() {
        if (window.PXUTheme && window.PXUTheme.currency && window.PXUTheme.currency.show_multiple_currencies) {
          window.currencyConverter.convertCurrencies();
        }
      },
      function() { window.productPage.init(); },
      function() { window.productPage.runOptionSelector($items); },
      function() { if (window.Shopify && Shopify.PaymentButton) Shopify.PaymentButton.init(); },
      function() { if (settings.quick_shop_enabled) window.quickShop.init(); },
      function() { window.hideNoScript(); },
      function() { window.videoFeature.init(); },
      function() { if (settings.collection_secondary_image) window.imageFunctions.showSecondaryImage(); }
    ];
    // One missing global from a third-party script must not abandon the restore.
    for (var i = 0; i < steps.length; i++) {
      try { steps[i](); } catch (e) { /* keep going */ }
    }
  }

  function fetchPage(page) {
    return fetch(pageUrl(page), {
      credentials: 'same-origin',
      headers: { 'X-Requested-With': 'XMLHttpRequest' }
    }).then(function(response) {
      if (!response.ok) throw new Error('HTTP ' + response.status);
      return response.text();
    }).then(function(html) {
      return new DOMParser().parseFromString(html, 'text/html');
    });
  }

  function restore(state) {
    var node = grid();
    if (!node || !itemCount()) {
      settle(state);
      return;
    }

    var pages = Math.min(state.pages || 1, MAX_PAGES);
    if (pages < 2) {
      settle(state);
      return;
    }

    var requests = [];
    for (var page = 2; page <= pages; page++) requests.push(fetchPage(page));

    showLoading(true);

    Promise.all(requests).then(function(docs) {
      var appended = [];
      var wrap = document.querySelector(MORE_WRAP);

      docs.forEach(function(doc) {
        var source = doc.querySelector(GRID);
        if (!source) return;
        var items = source.querySelectorAll(ITEM);
        for (var i = 0; i < items.length; i++) {
          var item = document.importNode(items[i], true);
          node.appendChild(item);
          appended.push(item);
        }
      });

      // The load more link has to stay last in the grid, and point at the page
      // after the ones just replayed.
      var lastDoc = docs[docs.length - 1];
      var nextLink = lastDoc ? lastDoc.querySelector(MORE_LINK) : null;
      var currentLink = moreLink();
      if (nextLink && currentLink) {
        currentLink.setAttribute('href', nextLink.getAttribute('href'));
        if (wrap) node.appendChild(wrap);
      } else if (wrap) {
        wrap.parentNode.removeChild(wrap);
      }

      showLoading(false);
      reinitialiseProducts(window.jQuery ? window.jQuery(appended) : appended);

      // The shopper had already switched this grid over to infinite scrolling,
      // so put it back in that mode rather than showing the button again.
      if (pagination === 'load_more' || pagination === 'infinite_scroll') {
        if (window.jQuery) window.jQuery(MORE_WRAP).hide();
        if (typeof window.enableInfiniteScroll === 'function' && moreLink()) {
          try { window.enableInfiniteScroll('.product-list'); } catch (e) { /* leave the button */ }
        }
      }

      settle(state);
    }).catch(function() {
      showLoading(false);
      settle(state);
    });
  }

  if (restoreState) {
    coverShownAt = Date.now() - (window.performance && window.performance.now ? window.performance.now() : 0);
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', function() { restore(restoreState); });
    } else {
      restore(restoreState);
    }
  } else if (coverIsUp()) {
    // theme.liquid paints the cover on a cheaper test than this file can make -
    // it cannot tell whether the saved state still matches this exact URL. When
    // it does not, nothing is going to be replayed, so get out of the way.
    hideCurtain();
  }
}());
